import { Alert, AlertIcon, Box, Flex, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbChevronRight } from 'react-icons/tb';
import { useGetIntegrations, type IntegrationsResponse } from '../../../hooks/useGetIntegrations';
import SessionContext, { isInvoicingOrgSession } from '../../../contexts/SessionContext';
import { canAccessIntegrations } from '../../../utils/integrationsAccess';
import { SettingsCard } from '../primitives';
import { PARTBAY_BLUE } from '../primitives/constants';
import ConnectionBadge from './ConnectionBadge';
import {
    CONNECTIONS,
    describeCoverage,
    getConnectionStatus,
    getVisibleConnectionGroups,
    type ConnectionId,
} from './catalog';

/** One connection, as a full-width row inside its group's card. */
const ConnectionRow = ({
    id,
    data,
    onSelect,
}: {
    id: ConnectionId;
    data: IntegrationsResponse;
    onSelect: (id: ConnectionId) => void;
}) => {
    const definition = CONNECTIONS[id];
    const status = getConnectionStatus(id, data);
    const coverage = describeCoverage(status);

    return (
        <Flex
            as="button"
            type="button"
            w="100%"
            textAlign="left"
            align="center"
            gap={4}
            px={5}
            py={4}
            role="group"
            transition="background 0.15s"
            _hover={{ bg: 'gray.50' }}
            onClick={() => onSelect(id)}
        >
            <ConnectionBadge id={id} />
            <Box flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    {definition.label}
                </Text>
                <Text
                    fontSize="sm"
                    color={status.isConnected ? 'green.600' : 'gray.500'}
                    fontWeight={status.isConnected ? 'medium' : 'normal'}
                    mt={0.5}
                >
                    {status.isConnected ? 'Connected' : 'Not connected'}
                </Text>
            </Box>
            {coverage && (
                <Text fontSize="sm" color="gray.500" flexShrink={0}>
                    {coverage}
                </Text>
            )}
            <Icon
                as={TbChevronRight as React.ElementType}
                boxSize={5}
                color="gray.400"
                flexShrink={0}
                _groupHover={{ color: 'gray.600' }}
            />
        </Flex>
    );
};

/**
 * Landing screen for Settings → Connections: every platform Yaxle can talk
 * to, grouped by what it does, with connected state derived from whether any
 * credential is stored. Clicking a row opens that connection's own page, where
 * the credentials themselves live.
 */
const ConnectionsSection = () => {
    const { data, isLoading, isError, error } = useGetIntegrations();
    const sessionContext = useContext(SessionContext);
    const navigate = useNavigate();

    // Defense in depth: the Settings nav already hides this section from
    // everyone but internal admins, but never render credentials if reached directly.
    if (!canAccessIntegrations(sessionContext)) {
        return (
            <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Integrations are managed by Yaxle. Contact support@yaxle.com to make changes.
            </Alert>
        );
    }

    if (isLoading) {
        return (
            <Flex justify="center" py={12}>
                <Spinner size="lg" color={PARTBAY_BLUE} />
            </Flex>
        );
    }

    if (isError) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error?.message || 'Failed to load connections.'}
            </Alert>
        );
    }

    if (!data) return null;

    const groups = getVisibleConnectionGroups(data, {
        invoicingOnly: isInvoicingOrgSession(sessionContext),
    });

    return (
        <VStack spacing={8} align="stretch">
            {groups.map((group) => (
                <Box key={group.id}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {group.label}
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        {group.description}
                    </Text>
                    <SettingsCard isFlush>
                        {group.connectionIds.map((id) => (
                            <ConnectionRow
                                key={id}
                                id={id}
                                data={data}
                                onSelect={(next) => navigate(`/settings/connections/${next}`)}
                            />
                        ))}
                    </SettingsCard>
                </Box>
            ))}
        </VStack>
    );
};

export default ConnectionsSection;
